/**
 * The presentation of the Golden Amphora (docs/TIBBS-VOICE.md 4): played only
 * when someone asks for it from the game-over screen. The script is written
 * live (like Single Didge's Medal Ceremony); each podium step rises, with its
 * names, when Tibbs reaches the sentence that presents that prize, so the
 * reveal keeps time with the voice. × skips to the finished podium.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { GameState } from '../../../engine';
import { useAssets } from '../../assets';
import { fmt } from '../../brand';
import type { SeatInfo } from '../../game/types';
import { label, listNames, totalOf } from '../../game/viewModel';
import { reducedMotion } from '../../lib/motion';
import type { Podium, Reveal } from '../../tibbs/ceremony';
import type { TibbsStatus } from '../../tibbs/useTibbs';
import { Crest, Pic, PlayerMedallion, Tibbs } from '../Art';
import type { TibbsPose } from '../Art';
import { AmphoraIcon, IconClose, IconHome, IconScores } from '../Icons';
import { TibbsCaption } from './TibbsButton';

/** The Scenic Route Award: a small clay tortoise, drawn in code. */
export function ClayTortoise({ size = 40 }: { size?: number }) {
  return (
    <svg className="clay-tortoise" viewBox="0 0 64 48" width={size} height={(size * 48) / 64} aria-hidden="true">
      <ellipse cx="32" cy="42" rx="24" ry="4" fill="rgba(60,40,20,0.18)" />
      <path d="M50 30 q9 -1 10 -7 q-1 -5 -7 -3 q-3 2 -5 5z" fill="#c4774a" stroke="#8f4f2a" strokeWidth="1.5" />
      <circle cx="56.5" cy="22.5" r="1.3" fill="#3b2414" />
      <path d="M13 37 l-3 6 h7 l1 -5z M23 39 l-1 5 h7 l-1 -5z M40 39 l-1 5 h7 l-1 -5z M50 36 l1 6 h7 l-3 -6z" fill="#b8683c" stroke="#8f4f2a" strokeWidth="1.2" />
      <path d="M8 36 q0 -22 24 -24 q24 2 24 24z" fill="#cf8452" stroke="#8f4f2a" strokeWidth="1.8" />
      <path d="M8 36 h48" stroke="#8f4f2a" strokeWidth="1.8" />
      <path d="M20 35 q2 -11 12 -12 q10 1 12 12 M32 23 v12 M14 31 q4 -9 10 -12 M50 31 q-4 -9 -10 -12" fill="none" stroke="#a65d33" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8 34 q-5 1 -6 4" fill="none" stroke="#8f4f2a" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Laurels() {
  const bits = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1,
        dur: 1.8 + Math.random() * 1.2,
        size: 10 + Math.random() * 8,
        rot: Math.random() * 360,
        gold: i % 3 === 0,
      })),
    [],
  );
  return (
    <div className="laurels" aria-hidden="true">
      {bits.map((b, i) => (
        <span
          key={i}
          className={`leaf${b.gold ? ' gold' : ''}`}
          style={{ left: `${b.left}%`, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, width: b.size, height: b.size * 0.5, '--r': `${b.rot}deg` } as CSSProperties}
        />
      ))}
    </div>
  );
}

export function Ceremony({
  state,
  seats,
  me,
  mode,
  isHost,
  podium,
  reveal,
  loadingText,
  caption,
  pose,
  status,
  onSkip,
  onClose,
  onPlayAgain,
  onHome,
  onScores,
}: {
  state: GameState;
  seats: SeatInfo[];
  me: number | null;
  mode: 'solo' | 'online';
  isHost: boolean;
  podium: Podium;
  reveal: Reveal;
  loadingText?: string;
  caption: string | null;
  pose: TibbsPose | null;
  status: TibbsStatus;
  onSkip: () => void;
  onClose: () => void;
  onPlayAgain: () => void;
  onHome: () => void;
  onScores: () => void;
}) {
  const a = useAssets();
  const finished = status === 'done' || status === 'idle' || status === 'failed';
  const twoPlayer = state.players.length === 2;
  const goldUp = finished || reveal.gold;
  const scenicUp = finished || reveal.scenic;
  const shownTier = (seat: number) =>
    finished ||
    (podium.gold.includes(seat) && reveal.gold) ||
    (podium.silver.includes(seat) && reveal.silver) ||
    (podium.bronze.includes(seat) && reveal.bronze) ||
    (podium.scenic.includes(seat) && reveal.scenic);
  const shown = (seat: number) => shownTier(seat);
  const name = (seat: number) => label(seats, me, seat);
  const names = (list: number[]) => listNames(list.filter(shown).map(name));
  const total = (seat: number) => fmt(totalOf(state.players[seat]));
  const scenicSeats = podium.scenic;
  const iWon = me !== null && podium.gold.includes(me);
  const [laurels, setLaurels] = useState(false);

  useEffect(() => {
    if (!goldUp || reducedMotion()) return;
    setLaurels(true);
    const t = setTimeout(() => setLaurels(false), 3200);
    return () => clearTimeout(t);
  }, [goldUp]);

  const step = (kind: 'gold' | 'silver' | 'bronze', list: number[], up: boolean) => (
    <div className={`cer-step ${kind}${up ? ' up' : ''}`}>
      <div className="cer-who">
        {up &&
          list.map((s) => (
            <span key={s} className={`cer-name${shown(s) ? ' on' : ''}`}>
              <PlayerMedallion seat={s} size={26} />
              <span className="cer-name-text">{shown(s) ? name(s) : ' '}</span>
            </span>
          ))}
      </div>
      {kind === 'gold' && up && (
        <div className="cer-trophy">
          <Pic img={iWon ? a.trophyWin : (a.trophyOther ?? a.trophyWin)} eager fallback={<AmphoraIcon size={56} />} />
        </div>
      )}
      <div className="cer-block">
        <span className="cer-place num">{kind === 'gold' ? 1 : kind === 'silver' ? 2 : 3}</span>
        {up && list.length > 0 && <span className="cer-total num">{list.map(total).join(' · ')}</span>}
      </div>
    </div>
  );

  const world = a.worlds.get(9);
  return (
    <div className="ceremony" role="dialog" aria-modal="true" aria-labelledby="cer-title">
      {world?.travel && (
        <div className="cer-bg" aria-hidden="true">
          <Pic img={world.travelPhone ?? world.travel} eager />
        </div>
      )}
      {laurels && <Laurels />}
      <div className="cer-panel panel">
        <button type="button" className="icon-btn cer-x" onClick={finished ? onClose : onSkip} aria-label={finished ? 'Close the presentation' : 'Skip to the podium'}>
          <IconClose />
        </button>
        <p className="cer-kicker mark">The Old World Tour</p>
        <h2 id="cer-title" className="cer-title">
          Presentation of the Golden Amphora
        </h2>
        <div className={`cer-crest${reveal.crest || finished ? ' on' : ''}`}>
          <Crest width={84} />
        </div>

        {/* A step only for a prize someone won: a tie for last can leave no Silver Laurel. */}
        <div className={`cer-podium${twoPlayer || !podium.silver.length ? ' two' : ''}`}>
          {!twoPlayer && podium.silver.length > 0 && step('silver', podium.silver, finished || reveal.silver)}
          {step('gold', podium.gold, goldUp)}
          {!twoPlayer && podium.bronze.length > 0 && step('bronze', podium.bronze, finished || reveal.bronze)}
        </div>

        {scenicSeats.length > 0 && (
          <div className={`cer-scenic${scenicUp ? ' on' : ''}`}>
            <ClayTortoise size={44} />
            <span className="cer-scenic-label">
              <span className="cer-scenic-title">Scenic Route Award</span>
              <span className="cer-scenic-name">{scenicUp ? names(scenicSeats) || ' ' : ' '}</span>
            </span>
            {scenicUp && <span className="cer-total num">{scenicSeats.map(total).join(' · ')}</span>}
          </div>
        )}

        <div className="cer-voice">
          {!finished && <Tibbs key={pose ?? 'wry'} pose={pose ?? 'wry'} size={34} className="cer-tibbs" />}
          <TibbsCaption text={finished ? null : (caption ?? (status === 'loading' ? (loadingText ?? null) : null))} />
        </div>

        {finished && (
          <div className="cer-actions">
            {mode === 'solo' || isHost ? (
              <button className="btn btn-gold btn-big" onClick={onPlayAgain} data-autofocus>
                Play again
              </button>
            ) : (
              <span className="ready-wait">
                <span className="spinner small" aria-hidden="true" /> Waiting for the host…
              </span>
            )}
            <div className="go-secondary">
              <button className="btn btn-ink btn-sm" onClick={onClose}>
                Standings
              </button>
              <button className="btn btn-ink btn-sm" onClick={onHome}>
                <IconHome /> Home
              </button>
              <button className="icon-btn go-sc" onClick={onScores} aria-label="Final scorecard">
                <IconScores />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
